/**
 * map — the English half, typed against the German module, so a
 * missing or misspelled key is a type error in this file.
 */
import type { map as de_map } from '../de/map';

export const map: Record<keyof typeof de_map, string> = {
  /* ---------------- map ---------------- */
  'map.title': 'Route map',
  'map.loading': 'Loading the map …',
  'map.error': 'The map cannot be shown here right now. The form still works.',
  'map.diag.stalled': 'The map has not loaded for {s} seconds.',
  'map.diag.nogl': 'This browser cannot draw a map.',
  'map.diag.tiles': 'Map data is unreachable right now.',

  'map.tool': 'Tool',
  'map.tool.add': 'Draw route',
  'map.tool.add.hint': 'A click adds a waypoint. Click the line to insert one between, right-click to delete.',
  'map.tool.info': 'Info',
  'map.tool.info.hint': 'A click shows what is under the cursor, airspace included.',
  'map.layers': 'Layers',
  'map.layer.airspaces': 'Airspaces',
  'map.layer.airports': 'Aerodromes',
  'map.layer.navaids': 'Navaids',
  'map.layer.reporting_points': 'Reporting points',
  'map.layer.obstacles': 'Obstacles',
  'map.layer.terrain': 'Terrain',
  'map.basemap': 'Base map',
  'map.base.avia': 'Standard',
  'map.base.aviaDark': 'Dark',
  'map.base.positron': 'Pale',
  'map.base.bright': 'Colourful',
  'map.base.liberty': 'Detailed',
  'map.fit': 'Fit route',

  'map.info.close': 'Close',
  'map.info.add': 'Add to route',
  'map.info.alt': 'Set as alternate',
  'map.info.alt.hint':
    'Writes the ICAO code into the form as the alternate. Runway, length, elevation and surface come from the aerodrome data.',
  'map.info.none': 'Nothing further published.',
  'map.info.unnamed': 'unnamed',
  'map.info.icao': 'ICAO',
  'map.info.ident': 'Ident',
  'map.info.kind': 'Kind',
  'map.info.type': 'Type',
  'map.info.class': 'Class',
  'map.info.limits': 'Lower – upper limit',
  'map.info.elev': 'Elevation',
  'map.info.top': 'Top elevation (MSL)',
  'map.info.height': 'Height above ground',
  'map.info.freq': 'Frequency',
  'map.info.rwy': 'Runways',
  'map.info.airport': 'Aerodrome',
  'map.info.country': 'Country',
  'map.info.compulsory': 'Compulsory reporting point',
  'map.info.optional': 'Reporting point on request',

  'map.kind.apt': 'Aerodrome',
  'map.kind.intl': 'International airport',
  'map.kind.mil': 'Military aerodrome',
  'map.kind.heli': 'Heliport',
  'map.kind.glider': 'Glider site',
  'map.kind.ul': 'Ultralight site',
  'map.kind.water': 'Water aerodrome',
  'map.kind.strip': 'Landing strip',
  'map.kind.agri': 'Agricultural strip',
  'map.kind.altiport': 'Altiport',
  'map.kind.closed': 'closed',

  'map.wp.title': 'Waypoints',
  'map.sheet.summary': 'Waypoints ({n}) · {nm} NM',
  'map.sheet.empty': 'Waypoints (0)',
  'map.sheet.grab': 'Waypoints – tap or drag',
  'map.wp.empty': 'No waypoints yet. Click the map, or search above by ICAO code or name.',
  'map.wp.emptyRO': 'This plan has no route saved.',
  'map.wp.name': 'Waypoint name',
  'map.wp.alt': 'ft',
  'map.wp.alt.aria': 'Planned altitude (ft MSL) at waypoint {n}',
  'map.wp.up': 'Move up',
  'map.wp.down': 'Move down',
  'map.wp.remove': 'Delete waypoint',
  'map.wp.menu': 'Waypoint {n}: menu',
  'map.wp.centre': 'Centre on the map',
  'map.wp.kind.airport': 'Aerodrome',
  'map.wp.kind.navaid': 'Navaid',
  'map.wp.kind.reporting_point': 'Reporting point',
  'map.wp.kind.user': 'free point',

  'map.search.ph': 'Search ICAO or name …',
  'map.search.aria': 'Search for an aerodrome, navaid or reporting point',
  'map.search.busy': 'searching …',
  'map.search.none': 'Nothing found.',

  'map.cap.legs': 'Only twelve legs fit the form – {n} more are flown but not filled in.',
  'map.cap.waypoints': 'A plan holds at most 60 waypoints.',

  'map.check.title': 'Airspace along the route',
  'map.check.hint': 'Advisory, not a clearance. Check AIP and NOTAM before the flight.',
  'map.check.busy': 'checking …',
  'map.check.total': 'Total distance {nm} NM',
  'map.check.leg': 'Leg {n}: {from} → {to} · {nm} NM',
  'map.check.clear': 'no airspace crossed at the planned altitude',
  'map.check.assumptions': 'What this check does not cover:',
  'map.assume.great_circle_leg': 'legs taken as the shortest path',
  'map.assume.gnd_as_zero': 'limits above ground treated as altitudes above sea level',
  'map.assume.std_fl_as_feet': 'flight levels taken as an altitude in feet',
  'map.assume.no_qnh_correction': 'no QNH correction',
  'map.assume.no_terrain': 'terrain not checked',
  'map.assume.dem_z10': 'terrain grid about 50 m',
  'map.assume.obstacle_top_is_lower_bound': 'obstacles may be taller than listed',
  'map.assume.msa_is_a_suggestion': 'the safety altitude is a suggestion, not a clearance',

  'map.field.synced': 'from the map',
  'map.field.differs': 'edited by hand – differs from the map',

  /* ---------------- openAIP data along the route (geo.ts) ---------------- */
  'geo.field.derived': 'from the aerodrome data {id}',
  'geo.credit': 'Aerodrome data by openAIP',
  // the values written into the remark column — short, because they are printed
  // into a narrow column of the paper form
  'geo.rp.compulsory': 'Compulsory reporting point',
  'geo.rp.optional': 'Reporting point',

  /* ---------------- winds aloft (winds.ts, source 'winds') ---------------- */
  'winds.title': 'Winds aloft',
  'winds.nolegs': 'No legs: place at least two waypoints on the map.',
  'winds.none': 'no wind',
  'winds.now.unset': 'Without a date and departure time the forecast is for now.',
  'winds.now.outside': 'There is no forecast for this departure time – the one for now applies.',
  'winds.unavailable': 'No forecast for leg {legs} — whatever is on the form stays there.',
  'winds.kept': 'W/V on leg {legs} was entered by hand and was not overwritten.',
  'winds.credit': 'Weather data by Open-Meteo.com',
  'winds.dropped': 'Only twelve legs fit the form – {n} without wind.',
  'winds.stale': 'out of date',
  'winds.stale.hint': 'Leg {legs} changed – the wind still describes the old track.',
  'winds.hint':
    'Forecast wind per leg. Planning aid, not a weather briefing.',
  'winds.status.hour': 'forecast {hour}',
  'winds.field.derived': 'from the wind, forecast {id}',
  'winds.field.derived.plain': 'from the wind',
};
